'use client';

import React, { FC } from 'react';
import styles from './SaleSection.module.scss';
import Wrapper from '@/src/components/ui/wrapper/Wrapper';
import Label from '@/src/components/ui/label/Label';
import {useInView} from "react-intersection-observer";
import createAnimationArr from "@/src/lib/helpers/createAnimationArr";
import NextImage from "@/src/components/ui/nextImage/NextImage";

const heading = 'Effective Promotion Blocks for Maximum Sales';
const excerpt =
    'Promote your best offers and bestselling products through customizable blocks that push customers toward quick action.';

const scrollImage = {
    image: '/images/themes/stockist/promo/scroll.webp',
    image2x: '/images/themes/stockist/promo/scroll@2x.webp',
};

const promoItems = [
    {
        image: '/images/themes/stockist/promo/pic-1.webp',
        title: 'Banners with Text & Links',
        text: 'Drive more sales by showcasing your message with banners featuring clear text and direct links, guiding customers effortlessly from interest to action.',
    },
    {
        image: '/images/themes/stockist/promo/pic-2.webp',
        title: 'Collections Showcase',
        text: 'Highlight your curated product collections with stunning visuals and organized layout that help customers discover and shop with ease.',
    },
    {
        image: '/images/themes/stockist/promo/pic-3.webp',
        title: 'Banner & Product Combo Layout',
        text: 'Engage customers with a bold promo banner on the left and a product showcase on the right, creating a balanced, attention-grabbing layout.',
    },
    {
        image: '/images/themes/stockist/promo/pic-4.webp',
        title: 'Product Carousel',
        text: 'Show products side-by-side in a smooth carousel, letting customers browse easily while keeping your store clean and engaging.',
    },
    {
        image: '/images/themes/stockist/promo/pic-5.webp',
        title: 'Expanded Collections with Promo Banner',
        text: 'Showcase a greater variety of collections alongside bold promo banner to capture attention and increase customer engagement and sales.',
    },
];

const SaleSection: FC = () => {

    const { ref: inViewRef2, inView: inView2 } = useInView({
        triggerOnce: true,
        rootMargin: '-250px 0px 0px',
        threshold: 0,
    });

    return (
        <section className={styles.saleSection}>
            <Wrapper>
                <div className={styles.saleSection__head} ref={inViewRef2}>
                    <Label>Sale-Focused Promo Section</Label>
                    <h2 className={`heading__2 ${styles.heading} ${inView2 && styles.animate__heading}`}>
                        {createAnimationArr(heading, styles.word__wrapper, 0, 0.01)}
                    </h2>
                    <p className={`excerpt ${styles.excerpt} ${inView2 && styles.animate__excerpt}`}>
                        {createAnimationArr(excerpt, styles.word__wrapper, 0.1, 0.01)}
                    </p>
                </div>
            </Wrapper>

            <div className={styles.saleSection__scroller}>
                <div className={styles.saleSection__list}>
                    {Array(3).fill(null).map((_, i) => (
                        <img
                            key={`scroll-1-${i}`}
                            srcSet={`${scrollImage.image2x} 2x, ${scrollImage.image} 1x`}
                            src={scrollImage.image2x}
                            alt=""
                        />
                    ))}
                </div>
                <div className={styles.saleSection__list} aria-hidden="true">
                    {Array(3).fill(null).map((_, i) => (
                        <img
                            key={`scroll-2-${i}`}
                            srcSet={`${scrollImage.image2x} 2x, ${scrollImage.image} 1x`}
                            src={scrollImage.image2x}
                            alt=""
                        />
                    ))}
                </div>
            </div>

            <Wrapper>
                <div className={styles.saleSection__promo}>
                    {promoItems.map((item, idx) => (
                        <div key={idx} className={styles.saleSection__item}>
                        <div className={styles.saleSection__pic}>
                                <NextImage
                                    src={item.image}
                                    alt={item.title}
                                    fill
                                    sizes="(max-width: 480px) 100vw, (max-width: 1280px) 50vw, 70vw"
                                    loading="lazy"
                                />
                            </div>
                            <div className={styles.saleSection__about}>
                                <h3 className={styles.saleSection__title}>{item.title}</h3>
                                <div className={styles.saleSection__text}>{item.text}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </Wrapper>
        </section>
    );
};

export default SaleSection;
